import type { LucideIcon } from "lucide-react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

/**
 * Workshop: Sidebar tree item — expandable folder/group row.
 */

interface SidebarTreeItemProps {
  label: string;
  icon: LucideIcon;
  expanded?: boolean;
  depth?: number;
  onToggle?: () => void;
  onClick?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function SidebarTreeItem({
  label,
  icon: Icon,
  expanded = false,
  depth = 0,
  onToggle,
  onClick,
  className,
  "data-testid": testId,
}: SidebarTreeItemProps) {
  return (
    <div
      className={cn("flex h-7 w-full items-center gap-1.5 transition-colors hover:bg-glass-stroke-subtle", className)}
      style={{ paddingLeft: `${16 + depth * 16}px`, paddingRight: "8px" }}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex shrink-0 items-center justify-center"
        aria-label={expanded ? "Collapse" : "Expand"}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <Icons.ChevronRight
          className={cn(
            "h-2.5 w-2.5 text-muted transition-transform",
            expanded && "rotate-90",
          )}
        />
      </button>
      <Icon className="h-3 w-3 shrink-0 text-muted" aria-hidden="true" />
      <button
        type="button"
        onClick={onClick}
        className="flex-1 truncate text-left text-xs text-secondary"
        data-testid={testId ? `${testId}-label` : undefined}
      >
        {label}
      </button>
    </div>
  );
}

export type { SidebarTreeItemProps };
